import { TenantConfig } from "../../../../../types/tenantConfig";

/**
 * The house check-in / check-out times printed on a full-day stay.
 *
 * A guest-apartment stay is booked by DATE — the stored timestamps carry no
 * meaningful time of day — so the summary prints a house rule rather than the
 * booking's own value. That rule used to be the literal `"15:00" : "11:00"`
 * inside `BookingSummaryCard`, shown to every tenant's customers with no way
 * for one whose flat is ready at noon to say otherwise.
 *
 * ── Why the numbers appear twice ──────────────────────────────────────────
 *
 * `new-backend/app/src/lib/tenantConfig.ts` (`DEFAULT_STAY_TIMES`) resolves
 * the same default and always emits both fields, so a loaded config is the
 * only thing that ever reaches the screen. The copy here covers the frames
 * before that config arrives, and a bundled tenant's build-time config, which
 * predates the field.
 *
 * The same default also stands in `backend/src/core/lib/stayTimes.ts` (the
 * mails) and `new-backend/admin/src/lib/stayTimes.ts` (when the pre-start
 * mails go out). The deployables cannot import each other, so this is the
 * sanctioned form: the copies name each other and a test pins them
 * (CLAUDE.md, "One Rule, One Place"). `stayTimes.contract.test.ts` asserts
 * these exact strings in every server copy; if it fails, they have drifted and
 * ALL move.
 *
 * ── The mails follow this too ─────────────────────────────────────────────
 *
 * All five guest-apartment templates take their hours from the same stored
 * value, via `backend/src/core/lib/stayTimesConfig.ts` and the
 * `%CHECKINTIMEDE%` / `%CHECKINTIMEEN%` placeholders. So a tenant that sets
 * `stayTimes` changes the screen AND its mails, which is the point — one time,
 * not two.
 *
 * An earlier version of this comment claimed the booking-updated mail said
 * 13:00 while the confirmation said 15:00, and called that a standing defect.
 * It was wrong: the 13:00 lives only in `backend/src/core/seeds/
 * KOLULA_settings_local.ts`, a DynamoDB-era seed that NOTHING imports, so it
 * has never reached a customer. Recorded here because the number is still
 * sitting in that file, and the next reader will find it the same way.
 */
export const FALLBACK_STAY_TIMES = {
  checkIn: "15:00",
  checkOut: "11:00",
} as const;

/** The configured hours, or today's default while the config is absent. */
export const resolveStayTimes = (
  config: TenantConfig | null | undefined
): { checkIn: string; checkOut: string } => ({
  checkIn: config?.stayTimes?.checkIn ?? FALLBACK_STAY_TIMES.checkIn,
  checkOut: config?.stayTimes?.checkOut ?? FALLBACK_STAY_TIMES.checkOut,
});
